import { 
  Container, 
  Typography, 
  Grid, 
  Box,
  Divider,
  Paper,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Card,
  CardContent,
  Button,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow
} from '@mui/material';
import Hero from '../components/common/Hero';
import { Helmet } from 'react-helmet-async';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import HomeWorkIcon from '@mui/icons-material/HomeWork';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import SchoolIcon from '@mui/icons-material/School';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import ParkIcon from '@mui/icons-material/Park';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import BusinessIcon from '@mui/icons-material/Business';
import ContactPageIcon from '@mui/icons-material/ContactPage';

const RealEstateInfo = () => {
  const neighborhoodFeatures = [
    "Family-friendly community with well-maintained homes",
    "Tree-lined streets",
    "Close proximity to shopping, dining, and recreation",
    "Excellent school district with top-rated schools",
    "Active HOA that maintains common areas and enforces covenants",
    "Community events throughout the year",
    "Strong sense of community and neighborliness"
  ];
  
  const faqs = [
    {
      question: "What are the HOA fees and what do they cover?",
      answer: "The annual HOA fee is $135 per year. This fee covers maintenance of common areas, community events, and administration of the HOA. Fees are typically due by January 31st of each year."
    },
    {
      question: "Are there architectural guidelines for the community?",
      answer: "Yes, all exterior modifications to homes including additions, painting, fences, and major landscaping changes must be approved by the Architectural Review Committee. These guidelines help maintain the aesthetic quality of our neighborhood and protect property values."
    },
    {
      question: "What schools serve Brynleigh Estates?",
      answer: "Brynleigh Estates is served by the Shelby County School District. The schools include Forest Oaks Elementary, Chelsea Middle School, and Chelsea High School. All are highly rated schools with excellent academic and extracurricular programs."
    },
    {
      question: "What is the process for selling my home in Brynleigh Estates?",
      answer: "When selling your home, please notify the HOA board. While not required, we recommend providing potential buyers with copies of the covenants and information about the HOA. A current homeowner packet can be requested from the board to provide to buyers."
    }
  ];
  
  const realtorContacts = [
    {
      name: "RealtySouth - Chelsea Office",
      phone: "(205) 678-8688",
      email: "chelseaoffice@realtysouth.com",
      website: "https://www.realtysouth.com/offices/chelsea"
    },
    {
      name: "ARC Realty",
      phone: "(205) 969-8910",
      email: "info@arcrealtyco.com",
      website: "https://arcrealtyco.com/"
    },
    {
      name: "Keller Williams Realty Metro South",
      phone: "(205) 271-7700",
      email: "info@kwmetrosouth.com",
      website: "https://www.kwmetrosouth.com/"
    }
  ];
  
  // Sample market data - in a real implementation, this would come from an API or database
  const marketData = [
    { year: "2025", avgPrice: "$378,000", daysOnMarket: 58, homes: 6 },
    { year: "2024", avgPrice: "$372,000", daysOnMarket: 42, homes: 7 },
    { year: "2023", avgPrice: "$385,000", daysOnMarket: 15, homes: 8 },
    { year: "2022", avgPrice: "$352,000", daysOnMarket: 12, homes: 11 },
    { year: "2021", avgPrice: "$335,000", daysOnMarket: 8, homes: 14 }
  ];

  return (
    <>
      <Helmet>
        <title>Real Estate Information | Brynleigh Estates Chelsea AL</title>
        <meta name="description" content="Information about real estate in Brynleigh Estates community in Chelsea, Alabama. Learn about neighborhood features, market trends, schools, and resources for buyers and sellers." />
        <link rel="canonical" href="https://brynleighestates.com/real-estate-info" />
        
        {/* Open Graph / Facebook */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://brynleighestates.com/real-estate-info" />
        <meta property="og:title" content="Real Estate Information | Brynleigh Estates Chelsea AL" />
        <meta property="og:description" content="Information about real estate in Brynleigh Estates community in Chelsea, Alabama. Learn about neighborhood features, market trends, and resources for buyers and sellers." />
        <meta property="og:image" content="https://brynleighestates.com/images/brynleigh-estates-social-logo.jpeg" />
        
        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Real Estate Information | Brynleigh Estates Chelsea AL" />
        <meta name="twitter:description" content="Real estate information for Brynleigh Estates in Chelsea, Alabama - market trends, schools, and more." />
        <meta name="twitter:image" content="https://brynleighestates.com/images/brynleigh-estates-social-logo.jpeg" />
      </Helmet>
      
      <Hero 
        imageUrl="/images/hills-bg.jpeg"
        title="Real Estate Information"
        subtitle="Property information for buyers and sellers"
      />
      
      <Container maxWidth="lg">
        <Box sx={{ my: 6 }}>
          <Typography variant="h4" gutterBottom fontWeight="medium" component="h1">
            Living in Brynleigh Estates
          </Typography>
          
          <Typography variant="body1" paragraph>
            Brynleigh Estates is a premier residential community in Chelsea, Alabama offering beautiful homes, 
            well-maintained common areas, and a strong sense of community. Our neighborhood is known for its 
            family-friendly atmosphere, excellent schools, and convenient location.
          </Typography>
          
          <Grid container spacing={4} sx={{ mt: 3 }}>
            <Grid item xs={12} md={6}>
              <Paper 
                elevation={0} 
                sx={{ 
                  p: 3, 
                  height: '100%',
                  borderRadius: 2,
                  border: '1px solid',
                  borderColor: 'divider'
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <HomeWorkIcon color="primary" sx={{ mr: 1.5 }} />
                  <Typography variant="h5" component="h2">
                    Neighborhood Features
                  </Typography>
                </Box>
                <Divider sx={{ mb: 2 }} />
                
                <List>
                  {neighborhoodFeatures.map((feature, index) => (
                    <ListItem key={index} sx={{ py: 0.5 }}>
                      <ListItemIcon sx={{ minWidth: 36 }}>
                        <CheckCircleOutlineIcon color="primary" fontSize="small" />
                      </ListItemIcon>
                      <ListItemText primary={feature} />
                    </ListItem>
                  ))}
                </List>
              </Paper>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <Paper 
                elevation={0} 
                sx={{ 
                  p: 3, 
                  height: '100%',
                  borderRadius: 2,
                  border: '1px solid',
                  borderColor: 'divider'
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <LocationOnIcon color="primary" sx={{ mr: 1.5 }} />
                  <Typography variant="h5" component="h2">
                    Location & Amenities
                  </Typography>
                </Box>
                <Divider sx={{ mb: 2 }} />
                
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <Card 
                      elevation={0} 
                      sx={{ 
                        mb: 2, 
                        border: '1px solid', 
                        borderColor: 'divider', 
                        height: '100%'
                      }}
                    >
                      <CardContent>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                          <SchoolIcon color="primary" fontSize="small" sx={{ mr: 1 }} />
                          <Typography variant="subtitle1" fontWeight="medium">
                            Schools
                          </Typography>
                        </Box>
                        <Typography variant="body2">
                          Forest Oaks Elementary (3.2mi)<br />
                          Chelsea Middle School (2.8mi)<br />
                          Chelsea High School (3.5mi)
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                  
                  <Grid item xs={12} sm={6}>
                    <Card 
                      elevation={0} 
                      sx={{ 
                        mb: 2, 
                        border: '1px solid', 
                        borderColor: 'divider',
                        height: '100%'
                      }}
                    >
                      <CardContent>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                          <DirectionsCarIcon color="primary" fontSize="small" sx={{ mr: 1 }} />
                          <Typography variant="subtitle1" fontWeight="medium">
                            Commute
                          </Typography>
                        </Box>
                        <Typography variant="body2">
                          Downtown Birmingham (22mi)<br />
                          Hoover (14mi)<br />
                          Hwy 280 Access (0.5mi)
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                  
                  <Grid item xs={12} sm={6}>
                    <Card 
                      elevation={0} 
                      sx={{ 
                        mb: 2, 
                        border: '1px solid', 
                        borderColor: 'divider',
                        height: '100%'
                      }}
                    >
                      <CardContent>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                          <ParkIcon color="primary" fontSize="small" sx={{ mr: 1 }} />
                          <Typography variant="subtitle1" fontWeight="medium">
                            Recreation
                          </Typography>
                        </Box>
                        <Typography variant="body2">
                          Chelsea Community Center (2.2mi)<br />
                          Heardmont Park (5.4mi)<br />
                          Chelsea Sports Complex (2.6mi)
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                  
                  <Grid item xs={12} sm={6}>
                    <Card 
                      elevation={0} 
                      sx={{ 
                        mb: 2, 
                        border: '1px solid', 
                        borderColor: 'divider',
                        height: '100%'
                      }}
                    >
                      <CardContent>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                          <BusinessIcon color="primary" fontSize="small" sx={{ mr: 1 }} />
                          <Typography variant="subtitle1" fontWeight="medium">
                            Shopping
                          </Typography>
                        </Box>
                        <Typography variant="body2">
                          Chelsea Corners (1.8mi)<br />
                          Inverness Plaza (8.2mi)<br />
                          The Summit (15mi)
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                </Grid>
              </Paper>
            </Grid>
          </Grid>
          
          <Box sx={{ mt: 6 }}>
            <Typography variant="h5" gutterBottom component="h2" align="center">
              Market Trends
            </Typography>
            <Divider sx={{ mb: 4 }} />
            
            <Paper 
              elevation={0} 
              sx={{ 
                p: { xs: 2, sm: 3 }, 
                borderRadius: 2,
                border: '1px solid',
                borderColor: 'divider',
                overflowX: 'auto'
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <AttachMoneyIcon color="primary" sx={{ mr: 1.5 }} />
                <Typography variant="h6">
                  Brynleigh Estates Real Estate Market Data
                </Typography>
              </Box>
              
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 'bold' }}>Year</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>Average Sale Price</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>Avg. Days on Market</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>Homes Sold</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {marketData.map((row) => (
                      <TableRow key={row.year}>
                        <TableCell>{row.year}</TableCell>
                        <TableCell>{row.avgPrice}</TableCell>
                        <TableCell>{row.daysOnMarket}</TableCell>
                        <TableCell>{row.homes}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              
              <Typography variant="caption" sx={{ display: 'block', mt: 2, textAlign: 'right' }}>
                *Data based on MLS listings. Current as of Jan 2026.
              </Typography>
            </Paper>
            
            <Typography variant="body2" color="text.secondary" sx={{ mt: 2, textAlign: 'center' }}>
              Market values continue to appreciate in our neighborhood, making Brynleigh Estates a solid investment.
            </Typography>
          </Box>
          
          <Box sx={{ mt: 6 }}>
            <Typography variant="h5" gutterBottom component="h2" align="center">
              Frequently Asked Questions
            </Typography>
            <Divider sx={{ mb: 4 }} />
            
            {faqs.map((faq, index) => (
              <Accordion 
                key={index} 
                elevation={0}
                sx={{ 
                  mb: 2, 
                  border: '1px solid', 
                  borderColor: 'divider',
                  '&:before': {
                    display: 'none',
                  },
                }}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  sx={{ 
                    backgroundColor: 'background.paper' 
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <HelpOutlineIcon color="primary" sx={{ mr: 1.5 }} />
                    <Typography fontWeight="medium">{faq.question}</Typography>
                  </Box>
                </AccordionSummary>
                <AccordionDetails>
                  <Typography>{faq.answer}</Typography>
                </AccordionDetails>
              </Accordion>
            ))}
          </Box>
          
          <Box sx={{ mt: 6 }}>
            <Typography variant="h5" gutterBottom component="h2" align="center">
              Local Realtors
            </Typography>
            <Divider sx={{ mb: 4 }} />
            
            <Grid container spacing={3}>
              {realtorContacts.map((realtor, index) => (
                <Grid item xs={12} md={4} key={index}>
                  <Paper 
                    elevation={0} 
                    sx={{ 
                      p: 3, 
                      height: '100%',
                      borderRadius: 2,
                      border: '1px solid',
                      borderColor: 'divider'
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <ContactPageIcon color="primary" sx={{ mr: 1.5 }} />
                      <Typography variant="h6" component="h3">
                        {realtor.name}
                      </Typography>
                    </Box>
                    <Divider sx={{ mb: 2 }} />
                    
                    <Typography variant="body2" paragraph>
                      <strong>Phone:</strong> {realtor.phone}
                    </Typography>
                    <Typography variant="body2" paragraph>
                      <strong>Email:</strong> {realtor.email}
                    </Typography>
                    
                    <Box sx={{ mt: 'auto', textAlign: 'center' }}>
                      <Button 
                        variant="outlined" 
                        color="primary"
                        size="small"
                        href={realtor.website}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        Visit Website
                      </Button>
                    </Box>
                  </Paper>
                </Grid>
              ))}
            </Grid>
            
            <Typography variant="body2" color="text.secondary" sx={{ mt: 3, textAlign: 'center' }}>
              Note: Brynleigh Estates HOA does not officially endorse any specific realtor. 
              This list is provided as a convenience for homeowners.
            </Typography>
          </Box>
          
          <Box 
            sx={{ 
              mt: 6, 
              p: 4, 
              textAlign: 'center',
              bgcolor: 'primary.light',
              color: 'primary.contrastText',
              borderRadius: 2
            }}
          >
            <Typography variant="h5" gutterBottom>
              Considering Selling or Buying?
            </Typography>
            <Typography variant="body1" paragraph>
              If you're considering selling your home or know someone interested in moving to our community, 
              we encourage you to share information about our neighborhood and HOA.
            </Typography>
            <Button 
              variant="contained" 
              color="secondary"
              size="large"
              href="/covenants"
            >
              View HOA Documents
            </Button>
          </Box>
        </Box>
      </Container>
    </>
  );
};

export default RealEstateInfo; 